import { escapeHtml } from '../utils/html-utils.js';
import { appendReturnTo } from '../utils/return-to-url.js';
import {
  getWidgetHostDomain,
  loadWidgetHostPreview,
  submitDraftPreviewToIframe,
  widgetHostUnavailableMessage
} from '../utils/widget-iframe-host.js';
import { extractWidgetFetchHosts, mergeAllowedHostsInput } from '../utils/widget-fetch-hosts.js';
import {
  createConfigFieldRow,
  schemaToConfigFields,
  configFieldsToSchema,
  validateConfigFields
} from '../utils/widget-config-schema.js';
import {
  renderPreviewConfigForm,
  readPreviewConfigFromDom
} from '../utils/widget-preview-config.js';

const PREVIEW_DEBOUNCE_MS = 300;

class WidgetManagement extends HTMLElement {
  constructor() {
    super();
    this.widgets = [];
    this.editingId = null;
    this.form = {
      name: '', description: '', htmlContent: '', cssContent: '', jsContent: '', allowedHosts: '',
      oauthAuthUrl: '', oauthTokenUrl: '', oauthScopes: '', oauthServiceLabel: '',
      oauthClientId: '', oauthClientSecret: '', oauthPkce: false,
      configFields: []
    };
    this.configSchemaError = '';
    this.previewRowId = null;
    this.rowPreviewCache = {};
    this.loading = true;
    this.saving = false;
    this.message = '';
    this._showNew = false;
    this._previewDebounceTimer = null;
    this.previewToken = null;
    this.previewTokenExpiresAt = 0;
    this.editorPreviewError = '';
    this.rowPreviewError = null;
    this.adminEditMeta = null;
    this._editingWidgetMeta = null;
    this._configSectionOpen = false;
    this.previewConfigValues = {};
  }

  connectedCallback() {
    this.render();
    this.loadWidgets();
  }

  disconnectedCallback() {
    if (this._previewDebounceTimer) {
      clearTimeout(this._previewDebounceTimer);
      this._previewDebounceTimer = null;
    }
  }

  async loadWidgets() {
    try {
      const res = await window.API.request('/widgets/mine');
      this.widgets = res.widgets || [];
      this.loading = false;
      this.render();
      this.attachListeners();
      await this.tryOpenEditFromQuery();
    } catch (err) {
      window.logger.error('Failed to load widgets:', err);
      this.loading = false;
      this.message = 'Failed to load widgets.';
      this.render();
    }
  }

  async tryOpenEditFromQuery() {
    const params = new URLSearchParams(window.location.search);
    const editId = parseInt(params.get('edit'), 10);
    if (Number.isNaN(editId) || editId < 1) return;

    const inMine = this.widgets.some((w) => w.id === editId);
    if (!inMine) {
      try {
        const res = await window.API.request(`/widgets/${editId}`);
        this.adminEditMeta = {
          name: res.widget.name,
          ownerUsername: res.widget.ownerUsername || `user #${res.widget.ownerUserId}`
        };
      } catch (err) {
        window.logger.warn('Could not open widget for admin edit:', err);
        return;
      }
    }

    await this.openEdit(editId);
  }

  capturePreviewConfigState() {
    if (this.previewRowId) {
      const key = `row-${this.previewRowId}`;
      this.previewConfigValues[key] = readPreviewConfigFromDom(this, key);
    }
    if (this.editingId || this._showNew) {
      this.previewConfigValues.editor = readPreviewConfigFromDom(this, 'editor');
    }
  }

  hideEditor() {
    this.capturePreviewConfigState();
    this.editingId = null;
    this._showNew = false;
    this.configSchemaError = '';
    this._configSectionOpen = false;
    this.previewToken = null;
    this.previewTokenExpiresAt = 0;
    this.editorPreviewError = '';
    this.adminEditMeta = null;
    this._editingWidgetMeta = null;
    this.form = {
      name: '', description: '', htmlContent: '', cssContent: '', jsContent: '', allowedHosts: '',
      oauthAuthUrl: '', oauthTokenUrl: '', oauthScopes: '', oauthServiceLabel: '',
      oauthClientId: '', oauthClientSecret: '', oauthPkce: false,
      configFields: []
    };
    if (this._previewDebounceTimer) {
      clearTimeout(this._previewDebounceTimer);
      this._previewDebounceTimer = null;
    }
  }

  resetForm() {
    this.editingId = null;
    this.form = {
      name: '', description: '', htmlContent: '', cssContent: '', jsContent: '', allowedHosts: '',
      oauthAuthUrl: '', oauthTokenUrl: '', oauthScopes: '', oauthServiceLabel: '',
      oauthClientId: '', oauthClientSecret: '', oauthPkce: false,
      configFields: []
    };
    this.configSchemaError = '';
    this._configSectionOpen = false;
    this._showNew = false;
    this.previewToken = null;
    this.previewTokenExpiresAt = 0;
    this.editorPreviewError = '';
    this.adminEditMeta = null;
    this._editingWidgetMeta = null;
    if (this._previewDebounceTimer) {
      clearTimeout(this._previewDebounceTimer);
      this._previewDebounceTimer = null;
    }
  }

  syncFormFromDom() {
    this.form.name = this.querySelector('[data-field="name"]')?.value || '';
    this.form.description = this.querySelector('[data-field="description"]')?.value || '';
    this.form.htmlContent = this.querySelector('[data-field="htmlContent"]')?.value || '';
    this.form.cssContent = this.querySelector('[data-field="cssContent"]')?.value || '';
    this.form.jsContent = this.querySelector('[data-field="jsContent"]')?.value || '';
    this.form.allowedHosts = this.querySelector('[data-field="allowedHosts"]')?.value || '';
    this.form.oauthAuthUrl = this.querySelector('[data-field="oauthAuthUrl"]')?.value || '';
    this.form.oauthTokenUrl = this.querySelector('[data-field="oauthTokenUrl"]')?.value || '';
    this.form.oauthScopes = this.querySelector('[data-field="oauthScopes"]')?.value || '';
    this.form.oauthServiceLabel = this.querySelector('[data-field="oauthServiceLabel"]')?.value || '';
    this.form.oauthClientId = this.querySelector('[data-field="oauthClientId"]')?.value || '';
    this.form.oauthClientSecret = this.querySelector('[data-field="oauthClientSecret"]')?.value || '';
    this.form.oauthPkce = Boolean(this.querySelector('[data-field="oauthPkce"]')?.checked);
    this.syncConfigFieldsFromDom();
  }

  syncConfigFieldsFromDom() {
    const rows = this.querySelectorAll('[data-config-row]');
    if (!rows.length) return;

    this.form.configFields = Array.from(rows).map((rowEl) => {
      const rowId = rowEl.dataset.configRow;
      const existing = this.form.configFields.find((row) => row.rowId === rowId);
      return {
        rowId: rowId || existing?.rowId || createConfigFieldRow().rowId,
        key: rowEl.querySelector('[data-config-field="key"]')?.value || '',
        title: rowEl.querySelector('[data-config-field="title"]')?.value || '',
        description: rowEl.querySelector('[data-config-field="description"]')?.value || '',
        required: Boolean(rowEl.querySelector('[data-config-field="required"]')?.checked),
        secret: Boolean(rowEl.querySelector('[data-config-field="secret"]')?.checked),
        markdown: Boolean(rowEl.querySelector('[data-config-field="markdown"]')?.checked)
      };
    });
  }

  captureConfigSectionOpen() {
    const el = this.querySelector('[data-config-section]');
    if (el) {
      this._configSectionOpen = el.dataset.open === 'true';
    }
  }

  addConfigField() {
    this.syncFormFromDom();
    this.captureConfigSectionOpen();
    this._configSectionOpen = true;
    this.form.configFields.push(createConfigFieldRow());
    this.configSchemaError = '';
    this.render();
    this.attachListeners();
  }

  removeConfigField(rowId) {
    this.syncFormFromDom();
    this.captureConfigSectionOpen();
    this.form.configFields = this.form.configFields.filter((row) => row.rowId !== rowId);
    this.configSchemaError = '';
    this.render();
    this.attachListeners();
  }

  buildConfigSchemaPayload() {
    const validation = validateConfigFields(this.form.configFields);
    if (!validation.valid) {
      throw new Error(validation.error);
    }
    return { configSchema: configFieldsToSchema(this.form.configFields) };
  }

  buildOAuthPayload() {
    const authUrl = this.form.oauthAuthUrl.trim();
    const tokenUrl = this.form.oauthTokenUrl.trim();
    if (!authUrl && !tokenUrl && !this.form.oauthClientId.trim()) {
      return {};
    }
    const scopes = this.form.oauthScopes.split(/[\s,]+/).filter(Boolean);
    const payload = {
      oauthConfig: {
        authorizationUrl: authUrl,
        tokenUrl: tokenUrl,
        scopes,
        pkce: this.form.oauthPkce,
        serviceLabel: this.form.oauthServiceLabel.trim() || 'service'
      },
      oauthClientId: this.form.oauthClientId.trim() || null
    };
    if (this.form.oauthClientSecret.trim()) {
      payload.oauthClientSecret = this.form.oauthClientSecret.trim();
    }
    return payload;
  }

  getOAuthCallbackDisplayUrl() {
    return `${window.location.origin}/api/widgets/oauth/callback`;
  }

  getPreviewFieldsFromForm() {
    return {
      name: this.querySelector('[data-field="name"]')?.value || this.form.name || 'Widget preview',
      htmlContent: this.querySelector('[data-field="htmlContent"]')?.value ?? this.form.htmlContent,
      cssContent: this.querySelector('[data-field="cssContent"]')?.value ?? this.form.cssContent,
      jsContent: this.querySelector('[data-field="jsContent"]')?.value ?? this.form.jsContent,
      widgetId: this.editingId || ''
    };
  }

  async ensurePreviewToken() {
    const now = Date.now();
    if (this.previewToken && this.previewTokenExpiresAt > now + 5000) {
      return this.previewToken;
    }
    const res = this.editingId
      ? await window.API.request(`/widgets/${this.editingId}/preview-token`, { method: 'POST' })
      : await window.API.request('/widgets/preview/draft-token', { method: 'POST' });
    this.previewToken = res.token;
    this.previewTokenExpiresAt = now + (res.expiresInSeconds || 300) * 1000;
    return this.previewToken;
  }

  async ensureRowPreviewToken(widgetId) {
    const res = await window.API.request(`/widgets/${widgetId}/preview-token`, { method: 'POST' });
    return res.token;
  }

  async updateEditorPreview() {
    const iframe = this.querySelector('.widget-editor-preview-frame');
    if (!iframe) return;
    try {
      const token = await this.ensurePreviewToken();
      this.editorPreviewError = '';
      submitDraftPreviewToIframe(iframe, token, this.getPreviewFieldsFromForm(), this, {
        getInstanceConfig: () => readPreviewConfigFromDom(this, 'editor'),
        onError: () => {
          this.editorPreviewError = widgetHostUnavailableMessage();
          this.updatePreviewErrorDom('editor');
        }
      });
    } catch (err) {
      window.logger.warn('Editor preview failed:', err);
      this.editorPreviewError = widgetHostUnavailableMessage();
      this.updatePreviewErrorDom('editor');
    }
  }

  updatePreviewErrorDom(scope) {
    if (scope === 'editor') {
      const el = this.querySelector('[data-preview-error="editor"]');
      if (el) {
        el.textContent = this.editorPreviewError;
        el.hidden = !this.editorPreviewError;
      }
    }
    if (scope === 'row' && this.previewRowId) {
      const el = this.querySelector(`[data-preview-error="row-${this.previewRowId}"]`);
      if (el) {
        el.textContent = this.rowPreviewError || '';
        el.hidden = !this.rowPreviewError;
      }
    }
  }

  scheduleEditorPreviewUpdate() {
    if (this._previewDebounceTimer) {
      clearTimeout(this._previewDebounceTimer);
    }
    this._previewDebounceTimer = setTimeout(() => {
      this._previewDebounceTimer = null;
      this.updateEditorPreview();
    }, PREVIEW_DEBOUNCE_MS);
  }

  async updateRowPreviewIframe(widgetId) {
    const iframe = this.querySelector(`.widget-row-preview-frame[data-widget-id="${widgetId}"]`);
    if (!iframe) return;
    try {
      const token = await this.ensureRowPreviewToken(widgetId);
      this.rowPreviewError = null;
      const host = getWidgetHostDomain();
      loadWidgetHostPreview(
        iframe,
        `${host}/render/preview/${widgetId}?token=${encodeURIComponent(token)}`,
        {
          widgetId,
          getInstanceConfig: () => readPreviewConfigFromDom(this, `row-${widgetId}`),
          onError: () => {
            this.rowPreviewError = widgetHostUnavailableMessage();
            this.updatePreviewErrorDom('row');
          }
        }
      );
      this.updatePreviewErrorDom('row');
    } catch (err) {
      window.logger.warn('Row preview failed:', err);
      this.rowPreviewError = widgetHostUnavailableMessage();
      this.updatePreviewErrorDom('row');
    }
  }

  async toggleRowPreview(id) {
    if (this.previewRowId === id) {
      this.capturePreviewConfigState();
      this.previewRowId = null;
      this.rowPreviewError = null;
      this.render();
      this.attachListeners();
      return;
    }

    this.hideEditor();
    this.previewRowId = id;
    this.rowPreviewError = null;

    if (!this.rowPreviewCache[id]) {
      try {
        const res = await window.API.request(`/widgets/${id}`);
        const w = res.widget;
        this.rowPreviewCache[id] = {
          name: w.name,
          htmlContent: w.htmlContent || '',
          cssContent: w.cssContent || '',
          jsContent: w.jsContent || '',
          configSchema: w.configSchema || null
        };
      } catch (err) {
        window.logger.error(err);
        alert('Could not load widget preview');
        this.previewRowId = null;
        return;
      }
    }

    this.render();
    this.attachListeners();
    await this.updateRowPreviewIframe(id);
  }

  async openEdit(id) {
    try {
      this.previewRowId = null;
      this.previewToken = null;
      this.previewTokenExpiresAt = 0;
      this.editorPreviewError = '';
      this.configSchemaError = '';
      const res = await window.API.request(`/widgets/${id}`);
      const w = res.widget;
      this.editingId = id;
      this._editingWidgetMeta = {
        hasOAuthClientSecret: Boolean(w.hasOAuthClientSecret),
        rejectionReason: w.rejectionReason || null
      };
      this._showNew = true;
      this.form = {
        name: w.name,
        description: w.description || '',
        htmlContent: w.htmlContent || '',
        cssContent: w.cssContent || '',
        jsContent: w.jsContent || '',
        allowedHosts: (w.allowedHosts || []).join(', '),
        oauthAuthUrl: w.oauthConfig?.authorizationUrl || '',
        oauthTokenUrl: w.oauthConfig?.tokenUrl || '',
        oauthScopes: (w.oauthConfig?.scopes || []).join(' '),
        oauthServiceLabel: w.oauthConfig?.serviceLabel || '',
        oauthClientId: w.oauthClientId || '',
        oauthClientSecret: '',
        oauthPkce: Boolean(w.oauthConfig?.pkce),
        configFields: schemaToConfigFields(w.configSchema)
      };
      this.rowPreviewCache[id] = {
        name: w.name,
        htmlContent: w.htmlContent || '',
        cssContent: w.cssContent || '',
        jsContent: w.jsContent || '',
        configSchema: w.configSchema || null
      };
      this.render();
      this.attachListeners();
      await this.updateEditorPreview();
    } catch (err) {
      window.logger.error(err);
      alert('Could not load widget');
    }
  }

  async saveDraft() {
    this.syncFormFromDom();
    this.configSchemaError = '';
    try {
      this.buildConfigSchemaPayload();
    } catch (err) {
      this.configSchemaError = err.message || 'Invalid config variables';
      this.render();
      this.attachListeners();
      return;
    }
    this.saving = true;
    this.render();
    this.attachListeners();
    try {
      const body = {
        name: this.form.name,
        description: this.form.description,
        htmlContent: this.form.htmlContent,
        cssContent: this.form.cssContent,
        jsContent: this.form.jsContent,
        allowedHosts: this.form.allowedHosts,
        ...this.buildOAuthPayload(),
        ...this.buildConfigSchemaPayload()
      };
      if (this.editingId) {
        await window.API.request(`/widgets/${this.editingId}`, { method: 'PUT', body: JSON.stringify(body) });
        this.rowPreviewCache[this.editingId] = {
          name: this.form.name,
          htmlContent: this.form.htmlContent,
          cssContent: this.form.cssContent,
          jsContent: this.form.jsContent,
          configSchema: this.buildConfigSchemaPayload().configSchema || null
        };
      } else {
        const res = await window.API.request('/widgets', { method: 'POST', body: JSON.stringify(body) });
        this.editingId = res.widget.id;
        this.previewToken = null;
        this.previewTokenExpiresAt = 0;
        this.rowPreviewCache[this.editingId] = {
          name: this.form.name,
          htmlContent: this.form.htmlContent,
          cssContent: this.form.cssContent,
          jsContent: this.form.jsContent,
          configSchema: this.buildConfigSchemaPayload().configSchema || null
        };
      }
      this.message = 'Saved.';
      this.hideEditor();
      await this.loadWidgets();
    } catch (err) {
      alert(err.message || 'Save failed');
    } finally {
      this.saving = false;
      this.render();
      this.attachListeners();
    }
  }

  async shareToCommunityCatalog() {
    if (!this.editingId) {
      await this.saveDraft();
    }
    if (!this.editingId) return;
    try {
      await window.API.request(`/widgets/${this.editingId}/submit-to-catalog`, { method: 'POST' });
      this.message = 'Submitted to community catalog!';
      this.resetForm();
      await this.loadWidgets();
    } catch (err) {
      alert(err.message || 'Submit failed');
    }
  }

  async submitForReview() {
    return this.shareToCommunityCatalog();
  }

  async deleteWidget(id) {
    if (!confirm('Delete this widget?')) return;
    try {
      await window.API.request(`/widgets/${id}`, { method: 'DELETE' });
      if (this.editingId === id) this.resetForm();
      if (this.previewRowId === id) this.previewRowId = null;
      delete this.rowPreviewCache[id];
      await this.loadWidgets();
    } catch (err) {
      alert(err.message || 'Delete failed');
    }
  }

  systemCatalogBadge(widget) {
    const sc = widget.systemCatalogStatus || 'none';
    if (sc === 'none') {
      return '<span class="widget-status-badge widget-status-personal">personal</span>';
    }
    return this.statusBadge(sc);
  }

  canDeleteWidget(widget) {
    const sc = widget.systemCatalogStatus || 'none';
    return sc === 'none' || sc === 'rejected';
  }

  canShareToCatalog(widget) {
    const sc = widget.systemCatalogStatus || 'none';
    return sc === 'none' || sc === 'rejected';
  }

  statusBadge(status) {
    return `<span class="widget-status-badge widget-status-${status}">${escapeHtml(status)}</span>`;
  }

  renderRejectionReason(widget) {
    if (!widget.rejectionReason) {
      return '';
    }
    return `
      <div class="widget-rejection-bubble" role="status">
        <p class="widget-rejection-bubble-label">Rejected Reason</p>
        <p class="widget-rejection-bubble-body">${escapeHtml(widget.rejectionReason)}</p>
      </div>
    `;
  }

  renderWidgetDetails(widget) {
    const lines = [];
    if (widget.description) {
      lines.push(`<div class="form-text">${escapeHtml(widget.description)}</div>`);
    }
    if (widget.rejectionReason) {
      lines.push(this.renderRejectionReason(widget));
    }
    return lines.join('');
  }

  docHref(path) {
    return appendReturnTo(path);
  }

  docLink(path, innerHtml, className = '') {
    const classAttr = className ? ` class="${className}"` : '';
    return `<a href="${this.docHref(path)}"${classAttr} target="_docs" rel="noopener noreferrer">${innerHtml}</a>`;
  }

  renderConfigFieldRow(row, index) {
    const rowTitle = row.title?.trim() || row.key?.trim() || `Variable ${index + 1}`;
    const markdownDisabled = row.secret ? 'disabled' : '';
    const secretDisabled = row.markdown ? 'disabled' : '';
    return `
      <div class="widget-config-field-row card" data-config-row="${escapeHtml(row.rowId)}">
        <div class="widget-config-field-row-header">
          <span class="form-label">${escapeHtml(rowTitle)}</span>
          <button type="button" class="btn btn-secondary btn-sm" data-action="remove-config-field" data-row-id="${escapeHtml(row.rowId)}">Remove</button>
        </div>
        <div class="form-group">
          <label class="form-label" for="config-key-${escapeHtml(row.rowId)}">Variable name</label>
          <input
            id="config-key-${escapeHtml(row.rowId)}"
            type="text"
            class="form-control"
            data-config-field="key"
            placeholder="apikey"
            autocomplete="off"
            value="${escapeHtml(row.key)}"
          />
          <p class="form-text">Used in <code>fot.getConfig()</code> and <code>{ $secret: 'name' }</code>.</p>
        </div>
        <div class="form-group">
          <label class="form-label" for="config-title-${escapeHtml(row.rowId)}">Label</label>
          <input
            id="config-title-${escapeHtml(row.rowId)}"
            type="text"
            class="form-control"
            data-config-field="title"
            placeholder="Last.fm API key"
            autocomplete="off"
            value="${escapeHtml(row.title)}"
          />
        </div>
        <div class="form-group">
          <label class="form-label" for="config-desc-${escapeHtml(row.rowId)}">Help text</label>
          <input
            id="config-desc-${escapeHtml(row.rowId)}"
            type="text"
            class="form-control"
            data-config-field="description"
            placeholder="Optional hint for profile owners"
            autocomplete="off"
            value="${escapeHtml(row.description)}"
          />
        </div>
        <div class="widget-config-field-flags">
          <label class="widget-config-flag">
            <input type="checkbox" data-config-field="required" ${row.required ? 'checked' : ''} />
            Required
          </label>
          <label class="widget-config-flag">
            <input type="checkbox" data-config-field="secret" ${row.secret ? 'checked' : ''} ${secretDisabled} />
            Secret
          </label>
          <label class="widget-config-flag">
            <input type="checkbox" data-config-field="markdown" ${row.markdown ? 'checked' : ''} ${markdownDisabled} />
            Markdown
          </label>
        </div>
      </div>
    `;
  }

  renderConfigFieldsSection() {
    const rows = this.form.configFields || [];
    const rowsHtml = rows.length
      ? rows.map((row, index) => this.renderConfigFieldRow(row, index)).join('')
      : '<p class="form-text widget-config-empty">No config variables yet. Add one if profile owners need to supply settings at install time.</p>';

    return `
      <collapsible-section data-config-section summary="Config variables (optional)" hint="Install-time settings for profile owners"${this._configSectionOpen ? ' open' : ''}>
        <p class="form-text">Profile owners fill these in when they add your widget to their layout. Secret values are encrypted server-side. See ${this.docLink('/docs/widgets/config-and-secrets', 'Config &amp; secrets')}.</p>
        <p class="form-text widget-config-schema-error" data-config-schema-error role="alert"${this.configSchemaError ? '' : ' hidden'}>${escapeHtml(this.configSchemaError)}</p>
        <div class="widget-config-fields">
          ${rowsHtml}
        </div>
        <button type="button" class="btn btn-secondary widget-add-config-field-btn" data-action="add-config-field">Add variable</button>
      </collapsible-section>
    `;
  }

  renderList() {
    if (this.loading) {
      return '<div class="loading-state"><span class="spinner"></span></div>';
    }
    if (this.widgets.length === 0) {
      return `<p class="form-text">No widgets yet. ${this.docLink('/docs/widgets/getting-started', 'Read the guide')}</p>`;
    }
    return this.widgets.map((w) => {
      const isExpanded = this.previewRowId === w.id;
      const rowError = isExpanded && this.rowPreviewError ? this.rowPreviewError : '';
      const contextKey = `row-${w.id}`;
      const cached = this.rowPreviewCache[w.id];
      const previewConfigForm = isExpanded
        ? renderPreviewConfigForm(
          cached?.configSchema,
          contextKey,
          this.previewConfigValues[contextKey] || {}
        )
        : '';
      return `
      <div class="widget-list-item card${isExpanded ? ' widget-list-item-expanded' : ''}">
        <div class="widget-list-item-header">
          <div class="widget-list-info">
            <div class="widget-list-meta">
              <strong>${escapeHtml(w.name)}</strong>
              ${this.systemCatalogBadge(w)}
            </div>
            ${this.renderWidgetDetails(w)}
          </div>
          <div class="widget-list-actions">
            <button type="button" class="btn btn-secondary btn-sm" data-action="preview" data-id="${w.id}" aria-expanded="${isExpanded}">${isExpanded ? 'Hide preview' : 'Preview'}</button>
            <button type="button" class="btn btn-secondary btn-sm" data-action="edit" data-id="${w.id}">Edit</button>
            ${this.canDeleteWidget(w) ? `<button type="button" class="btn btn-secondary btn-sm" data-action="delete" data-id="${w.id}">Delete</button>` : ''}
          </div>
        </div>
        ${isExpanded ? `
          <div class="widget-row-preview">
            ${previewConfigForm}
            <p class="form-text widget-preview-error" data-preview-error="row-${w.id}" role="alert"${rowError ? '' : ' hidden'}>${escapeHtml(rowError)}</p>
            <iframe
              class="widget-row-preview-frame widget-preview-frame"
              name="widget-row-preview-${w.id}"
              data-preview-name="widget-row-preview-${w.id}"
              data-widget-id="${w.id}"
              title="Preview of ${escapeHtml(w.name)}"
              sandbox="allow-scripts allow-same-origin"
            ></iframe>
          </div>
        ` : ''}
      </div>
    `;
    }).join('');
  }

  renderEditor() {
    if (!this.editingId && !this._showNew) {
      return '';
    }
    const editingWidget = this.editingId
      ? (this.widgets.find((w) => w.id === this.editingId) || this._editingWidgetMeta)
      : null;
    const hasOAuthClientSecret = Boolean(
      editingWidget?.hasOAuthClientSecret ?? this._editingWidgetMeta?.hasOAuthClientSecret
    );
    const rejectionNote = editingWidget?.rejectionReason
      ? this.renderRejectionReason(editingWidget)
      : '';
    const adminBanner = this.adminEditMeta
      ? `<p class="form-text widget-admin-edit-banner" role="status">Editing <strong>${escapeHtml(this.adminEditMeta.name)}</strong> (owned by @${escapeHtml(this.adminEditMeta.ownerUsername)}).</p>`
      : '';
    return `
      <div class="widget-editor card">
        <h3>${this.editingId ? 'Edit widget' : 'New widget'}</h3>
        ${adminBanner}
        <p class="form-text">Need help? ${this.docLink('/docs/widgets/html-css-js', 'HTML/CSS/JS rules')} · ${this.docLink('/docs/widgets/message-channel', 'fot.fetch API')} · ${this.docLink('/docs/widgets/config-and-secrets', 'Config &amp; secrets')} · ${this.docLink('/docs/widgets/oauth', 'OAuth')} · ${this.docLink('/docs/widgets/submit-and-review', 'Submit &amp; review')}</p>
        ${rejectionNote}
        <div class="form-group">
          <label class="form-label">Name</label>
          <input type="text" class="form-control" data-field="name" maxlength="255" value="${escapeHtml(this.form.name)}" />
        </div>
        <div class="form-group">
          <label class="form-label">Description</label>
          <textarea class="form-control widget-description" data-field="description" rows="2">${escapeHtml(this.form.description)}</textarea>
        </div>
        <div class="widget-code-grid">
          <div class="form-group">
            <label class="form-label">HTML</label>
            <textarea class="form-control widget-code" data-field="htmlContent" rows="6">${escapeHtml(this.form.htmlContent)}</textarea>
          </div>
          <div class="form-group">
            <label class="form-label">CSS</label>
            <textarea class="form-control widget-code" data-field="cssContent" rows="6">${escapeHtml(this.form.cssContent)}</textarea>
          </div>
          <div class="form-group">
            <label class="form-label">JavaScript</label>
            <textarea class="form-control widget-code" data-field="jsContent" rows="6">${escapeHtml(this.form.jsContent)}</textarea>
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Allowed API hosts</label>
          <textarea class="form-control" data-field="allowedHosts" rows="2" placeholder="ws.audioscrobbler.com, last.fm">${escapeHtml(this.form.allowedHosts)}</textarea>
          <button type="button" class="btn btn-secondary widget-suggest-hosts-btn" data-action="suggest-hosts">Suggest hosts from my JS</button>
          <p class="form-text">Only these domains can be called via <code>fot.fetch</code>. Include API hosts and CDNs. See ${this.docLink('/docs/widgets/message-channel', 'fot.fetch')} and ${this.docLink('/docs/widgets/config-and-secrets', 'secrets')}.</p>
          <p class="form-text widget-suggest-hosts-hint" data-suggest-hosts-hint hidden></p>
        </div>
        ${this.renderConfigFieldsSection()}
        <collapsible-section summary="OAuth (optional)" hint="Connect button for profile owners">
          <p class="form-text">Register this redirect URI with your OAuth provider: <code>${escapeHtml(this.getOAuthCallbackDisplayUrl())}</code></p>
          <div class="form-group">
            <label class="form-label">Service label</label>
            <input type="text" class="form-control" data-field="oauthServiceLabel" placeholder="GitHub" value="${escapeHtml(this.form.oauthServiceLabel)}" />
          </div>
          <div class="form-group">
            <label class="form-label">Authorization URL</label>
            <input type="url" class="form-control" data-field="oauthAuthUrl" placeholder="https://github.com/login/oauth/authorize" value="${escapeHtml(this.form.oauthAuthUrl)}" />
          </div>
          <div class="form-group">
            <label class="form-label">Token URL</label>
            <input type="url" class="form-control" data-field="oauthTokenUrl" placeholder="https://github.com/login/oauth/access_token" value="${escapeHtml(this.form.oauthTokenUrl)}" />
          </div>
          <div class="form-group">
            <label class="form-label">Scopes</label>
            <input type="text" class="form-control" data-field="oauthScopes" placeholder="read:user" value="${escapeHtml(this.form.oauthScopes)}" />
          </div>
          <div class="form-group">
            <label class="form-label">OAuth client ID</label>
            <input type="text" class="form-control" data-field="oauthClientId" autocomplete="off" value="${escapeHtml(this.form.oauthClientId)}" />
          </div>
          <div class="form-group">
            <label class="form-label">OAuth client secret</label>
            <input type="password" class="form-control" data-field="oauthClientSecret" autocomplete="new-password" placeholder="${hasOAuthClientSecret ? 'Leave blank to keep existing' : ''}" />
            ${hasOAuthClientSecret ? '<p class="form-text">Leave blank when saving — your existing secret is kept.</p>' : ''}
          </div>
          <div class="form-group widget-oauth-pkce-row">
            <div class="widget-oauth-pkce-info">
              <span class="form-label">Use PKCE</span>
              <p class="form-text">Enable for providers that require Proof Key for Code Exchange.</p>
            </div>
            <label class="toggle-switch" aria-label="Use PKCE">
              <input type="checkbox" data-field="oauthPkce" ${this.form.oauthPkce ? 'checked' : ''} />
              <span class="toggle-slider"></span>
            </label>
          </div>
        </collapsible-section>
        <div class="widget-preview-block">
          <h4>Preview</h4>
          <p class="form-text">Updates live as you edit (sandboxed on widget-host). Save to persist changes.</p>
          ${renderPreviewConfigForm(
    null,
    'editor',
    this.previewConfigValues.editor || {},
    this.form.configFields
  )}
          <p class="form-text widget-preview-error" data-preview-error="editor" role="alert"${this.editorPreviewError ? '' : ' hidden'}>${escapeHtml(this.editorPreviewError)}</p>
          <iframe
            class="widget-editor-preview-frame widget-preview-frame"
            name="widget-editor-preview"
            data-preview-name="widget-editor-preview"
            title="Widget preview"
            sandbox="allow-scripts allow-same-origin"
          ></iframe>
        </div>
        <div class="widget-editor-actions">
          <button type="button" class="btn btn-primary" data-action="save" ${this.saving ? 'disabled' : ''}>Save</button>
          <button type="button" class="btn btn-secondary" data-action="submit" ${!this.editingId || (editingWidget && !this.canShareToCatalog(editingWidget)) ? 'disabled' : ''}>Share to community catalog</button>
          <button type="button" class="btn btn-secondary" data-action="cancel">Cancel</button>
        </div>
      </div>
    `;
  }

  render() {
    this.captureConfigSectionOpen();
    this.capturePreviewConfigState();
    this.innerHTML = `
      <div class="widget-management">
        <div class="widget-management-header">
          <h1>My Widgets</h1>
          <p>Create sandboxed blocks for your profile — use on your page right away, or share to the community catalog.</p>
          ${this.docLink('/docs/widgets', 'Widget docs', 'btn btn-secondary')}
          <button type="button" class="btn btn-primary" data-action="new">Create widget</button>
        </div>
        ${this.message ? `<p class="widget-message">${escapeHtml(this.message)}</p>` : ''}
        <div class="widget-list">${this.renderList()}</div>
        ${this.renderEditor()}
      </div>
    `;
  }

  attachListeners() {
    this.querySelector('[data-action="new"]')?.addEventListener('click', () => {
      this.resetForm();
      this._showNew = true;
      this.previewRowId = null;
      this.render();
      this.attachListeners();
      this.updateEditorPreview();
    });

    this.querySelectorAll('[data-action="preview"]').forEach((btn) => {
      btn.addEventListener('click', () => this.toggleRowPreview(parseInt(btn.dataset.id, 10)));
    });

    this.querySelectorAll('[data-action="edit"]').forEach((btn) => {
      btn.addEventListener('click', () => this.openEdit(parseInt(btn.dataset.id, 10)));
    });
    this.querySelectorAll('[data-action="delete"]').forEach((btn) => {
      btn.addEventListener('click', () => this.deleteWidget(parseInt(btn.dataset.id, 10)));
    });

    this.querySelector('[data-action="save"]')?.addEventListener('click', () => {
      this.syncFormFromDom();
      this.saveDraft();
    });

    this.querySelector('[data-action="submit"]')?.addEventListener('click', () => this.submitForReview());
    this.querySelector('[data-action="cancel"]')?.addEventListener('click', () => {
      this.resetForm();
      this.render();
      this.attachListeners();
    });

    this.querySelector('[data-action="add-config-field"]')?.addEventListener('click', () => {
      this.addConfigField();
    });

    this.querySelector('[data-config-section]')?.addEventListener('collapsible-toggle', (event) => {
      this._configSectionOpen = Boolean(event.detail?.open);
    });

    this.querySelectorAll('[data-action="remove-config-field"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.removeConfigField(btn.dataset.rowId);
      });
    });

    this.querySelectorAll('[data-config-row]').forEach((rowEl) => {
      const secretInput = rowEl.querySelector('[data-config-field="secret"]');
      const markdownInput = rowEl.querySelector('[data-config-field="markdown"]');
      secretInput?.addEventListener('change', () => {
        if (secretInput.checked && markdownInput) {
          markdownInput.checked = false;
          markdownInput.disabled = true;
        } else if (markdownInput) {
          markdownInput.disabled = false;
        }
      });
      markdownInput?.addEventListener('change', () => {
        if (markdownInput.checked && secretInput) {
          secretInput.checked = false;
          secretInput.disabled = true;
        } else if (secretInput) {
          secretInput.disabled = false;
        }
      });
    });

    this.querySelector('[data-action="suggest-hosts"]')?.addEventListener('click', () => {
      this.syncFormFromDom();
      const js = this.querySelector('[data-field="jsContent"]')?.value || '';
      const extracted = extractWidgetFetchHosts(js);
      const hostsField = this.querySelector('[data-field="allowedHosts"]');
      const hint = this.querySelector('[data-suggest-hosts-hint]');
      if (!extracted.length) {
        if (hint) {
          hint.hidden = false;
          hint.textContent = 'No fot.fetch URLs found — add hosts manually or use fot.fetch in your JS first.';
        }
        return;
      }
      if (hostsField) {
        hostsField.value = mergeAllowedHostsInput(hostsField.value, extracted);
        this.form.allowedHosts = hostsField.value;
      }
      if (hint) {
        hint.hidden = false;
        hint.textContent = `Added: ${extracted.join(', ')}`;
      }
    });

    this.querySelectorAll('[data-preview-config]').forEach((section) => {
      const contextKey = section.dataset.previewConfig;
      section.querySelectorAll('[data-preview-config-field]').forEach((field) => {
        field.addEventListener('input', () => {
          this.previewConfigValues[contextKey] = readPreviewConfigFromDom(this, contextKey);
          if (contextKey === 'editor') {
            this.scheduleEditorPreviewUpdate();
          } else if (contextKey.startsWith('row-')) {
            const widgetId = parseInt(contextKey.slice(4), 10);
            if (!Number.isNaN(widgetId)) {
              if (this._previewDebounceTimer) {
                clearTimeout(this._previewDebounceTimer);
              }
              this._previewDebounceTimer = setTimeout(() => {
                this._previewDebounceTimer = null;
                this.updateRowPreviewIframe(widgetId);
              }, PREVIEW_DEBOUNCE_MS);
            }
          }
        });
      });
    });

    if (this.editingId || this._showNew) {
      const previewFields = ['name', 'htmlContent', 'cssContent', 'jsContent'];
      previewFields.forEach((field) => {
        this.querySelector(`[data-field="${field}"]`)?.addEventListener('input', () => {
          this.scheduleEditorPreviewUpdate();
        });
      });
      this.updateEditorPreview();
    }

    if (this.previewRowId) {
      this.updateRowPreviewIframe(this.previewRowId);
    }
  }
}

customElements.define('widget-management', WidgetManagement);
